/**
 * XP & Level Progression System (@big2/shared)
 * 
 * Provides deterministic level, XP progress, and rank title calculations.
 * Levels are continuous and progression scales smoothly without hard caps.
 */

export interface LevelInfo {
  level: number;
  totalXp: number;
  currentLevelXp: number;
  nextLevelXp: number;
  progressPercent: number;
  rankTitle: string;
  rankBadge: string;
  tierColor: string;
}

export const XP_REWARDS = {
  GAME_PLAYED: 20,
  GAME_WON: 50,
  TOKEN_ROOM_MULTIPLIER: 1.5,
} as const;

/**
 * Calculates XP required to advance from level L to level L+1.
 * Level 1 -> 2: 100 XP
 * Level 2 -> 3: 150 XP
 * Level 3 -> 4: 200 XP
 * Level L -> L+1: 100 + (L - 1) * 50 XP
 */
export function xpRequiredForLevel(level: number): number {
  if (level < 1) return 100;
  return 100 + (level - 1) * 50;
}

/**
 * Total cumulative XP required to reach a given level from level 1.
 */
export function cumulativeXpForLevel(level: number): number {
  if (level <= 1) return 0;
  // Sum of (100 + (i - 1) * 50) for i = 1 to level - 1
  const n = level - 1;
  return 100 * n + (25 * n * (n - 1));
}

/**
 * Returns rank title, badge emoji, and theme color for a level.
 */
export function getRankTier(level: number, lang: 'mn' | 'en' = 'mn'): {
  title: string;
  badge: string;
  tierColor: string;
} {
  const isMn = lang === 'mn';
  if (level >= 80) {
    return {
      title: isMn ? 'Тэнгэрийн тоглогч' : 'Mythic Grandmaster',
      badge: '⚡',
      tierColor: '#f43f5e',
    };
  }
  if (level >= 60) {
    return {
      title: isMn ? 'Домогт' : 'Grandmaster',
      badge: '👑',
      tierColor: '#f59e0b',
    };
  }
  if (level >= 45) {
    return {
      title: isMn ? 'Их мастер' : 'Champion',
      badge: '🏆',
      tierColor: '#c084fc',
    };
  }
  if (level >= 30) {
    return {
      title: isMn ? 'Мастер' : 'Veteran',
      badge: '🎖️',
      tierColor: '#38bdf8',
    };
  }
  if (level >= 20) {
    return {
      title: isMn ? 'Туршлагатай' : 'Skilled',
      badge: '🥇',
      tierColor: '#fbbf24',
    };
  }
  if (level >= 10) {
    return {
      title: isMn ? 'Суралцагч' : 'Apprentice',
      badge: '🥈',
      tierColor: '#94a3b8',
    };
  }
  return {
    title: isMn ? 'Эхлэгч' : 'Beginner',
    badge: '🥉',
    tierColor: '#d97706',
  };
}

/**
 * Calculates current level, progress, and rank information from cumulative XP.
 */
export function calculateLevel(xp: number = 0, lang: 'mn' | 'en' = 'mn'): LevelInfo {
  const safeXp = Math.max(0, Math.floor(xp || 0));

  let level = 1;
  let accumulated = 0;

  // Determine current level
  while (true) {
    const needed = xpRequiredForLevel(level);
    if (accumulated + needed > safeXp) {
      const currentLevelXp = safeXp - accumulated;
      const progressPercent = Math.min(100, Math.round((currentLevelXp / needed) * 100));
      const rank = getRankTier(level, lang);
      return {
        level,
        totalXp: safeXp,
        currentLevelXp,
        nextLevelXp: needed,
        progressPercent,
        rankTitle: rank.title,
        rankBadge: rank.badge,
        tierColor: rank.tierColor,
      };
    }
    accumulated += needed;
    level += 1;
  }
}

/**
 * Calculates XP earned for completing a game.
 */
export function calculateMatchXp(won: boolean, isTsaiRoom: boolean = false): number {
  let xp: number = XP_REWARDS.GAME_PLAYED;
  if (won) xp += XP_REWARDS.GAME_WON;
  if (isTsaiRoom) {
    xp = Math.round(xp * XP_REWARDS.TOKEN_ROOM_MULTIPLIER);
  }
  return xp;
}
